import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Check, CheckCircle2, Clock, Eye, GitBranch, Mail, XCircle } from "lucide-react";
import { BrandChannelMark, hasBrandChannelMark } from "@/components/common/BrandChannelMark";
import { formatDateTime } from "@/lib/format";
import { chInfo, statusColor } from "@/pages/signals/rnc/rncData";
import { signalsDashboardPath, SIGNALS_TAB_RNC } from "@/pages/signals/signalsNav";
import "@/pages/signals/rnc/rncPlatform.css";

export function RncShell({ children, crumb }) {
  return (
    <div className="rnc-platform h-full min-h-0 min-w-0 overflow-y-auto overflow-x-clip scrollbar-thin" data-testid="rnc-platform">
      <div className="content">
        {crumb ? <div className="crumb">{crumb}</div> : null}
        {children}
      </div>
    </div>
  );
}

RncShell.propTypes = {
  children: PropTypes.node,
  crumb: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};

export function ChannelBadge({ channelKey, size = 22 }) {
  const c = chInfo(channelKey);
  if (hasBrandChannelMark(channelKey)) {
    return (
      <span
        title={c.name}
        className="channel-brand-badge"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          flexShrink: 0,
          // Keep native brand shape (rounded square / circle) — do not clip to a circle.
          lineHeight: 0,
          filter: "drop-shadow(0 1px 1px rgba(15, 23, 42, 0.12))",
        }}
        aria-label={c.name}
      >
        <BrandChannelMark channelKey={channelKey} size={size} />
      </span>
    );
  }
  return (
    <span
      title={c.name}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: "50%",
        background: `${c.color}1A`,
        color: c.color,
        fontSize: Math.round(size * 0.4),
        fontWeight: 800,
        flexShrink: 0,
      }}
    >
      {c.abbr}
    </span>
  );
}

ChannelBadge.propTypes = {
  channelKey: PropTypes.string.isRequired,
  size: PropTypes.number,
};

export function RncCrumb({ company }) {
  return (
    <>
      <Link to={signalsDashboardPath()} className="link" style={{ color: "inherit", textDecoration: "none" }}>Signals</Link>
      <span className="sep">/</span>
      <Link to={signalsDashboardPath(SIGNALS_TAB_RNC)} className="link" style={{ color: "inherit", textDecoration: "none" }}>RNC</Link>
      <span className="sep">/</span>
      <b>{company}</b>
    </>
  );
}

RncCrumb.propTypes = {
  company: PropTypes.string.isRequired,
};

export function FlowNode({ step, tone }) {
  if (step.kind === "wait") {
    return (
      <div className="fnode wait">
        <Clock size={14} style={{ display: "inline", marginRight: 4 }} /> {step.title}
      </div>
    );
  }
  if (step.kind === "goal") {
    return (
      <div className="fnode goal">
        <CheckCircle2 size={14} style={{ display: "inline", marginRight: 4 }} /> {step.title}
      </div>
    );
  }
  if (step.kind === "condition") {
    return (
      <div className="fnode condition">
        <GitBranch size={14} style={{ display: "inline", marginRight: 4 }} /> {step.title}
      </div>
    );
  }
  return (
    <div className={`fnode action ${tone || "grey"}`}>
      <div className="badge2">
        <Mail size={12} /> {step.badge}
      </div>
      <div className="ttl2">{step.title}</div>
      <div className="dt2">{step.detail}</div>
    </div>
  );
}

FlowNode.propTypes = {
  step: PropTypes.object.isRequired,
  tone: PropTypes.string,
};

export function FlowCanvas({ workflow, progressIndex = null }) {
  const steps = Array.isArray(workflow?.steps) ? workflow.steps : [];
  if (!steps.length) {
    return (
      <div className="flow-col" data-testid="flow-canvas-empty" style={{ padding: 24, color: "var(--slate-400)", fontSize: 14 }}>
        No workflow assigned — configure a campaign workflow to see steps here.
      </div>
    );
  }

  let flatIdx = 0;
  const nextTone = () => {
    if (progressIndex === null || progressIndex === undefined) return "grey";
    let t = "grey";
    if (flatIdx < progressIndex) {
      t = "green";
    } else if (flatIdx === progressIndex) {
      t = "blue";
    }
    flatIdx += 1;
    return t;
  };

  return (
    <div className="flow-col">
      {steps.map((s, idx) => {
        if (!s || typeof s !== "object") return null;
        if (s.kind === "condition") {
          const branches = Array.isArray(s.branches) ? s.branches : [];
          return (
            <div key={s.title || `cond-${idx}`} style={{ display: "contents" }}>
              <div className="connector" />
              <FlowNode step={s} />
              <div className="connector" />
              <div className="branchwrap">
                {branches.map((b, bi) => (
                  <div className="branchcol" key={`${s.title || idx}-${b?.label || bi}`}>
                    <div className="branchlabel">{b?.label || "—"}</div>
                    {b?.node ? <FlowNode step={b.node} tone={nextTone()} /> : null}
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key={`${s.kind}-${s.title || idx}`} style={{ display: "contents" }}>
            <div className="connector" />
            <FlowNode step={s} tone={s.kind === "action" ? nextTone() : undefined} />
          </div>
        );
      })}
    </div>
  );
}

FlowCanvas.propTypes = {
  workflow: PropTypes.object,
  progressIndex: PropTypes.number,
};

function getWorkflowDefaultStep(isTerminal, rncStatus, workflowAssigned, currentStep) {
  if (isTerminal) {
    return String(rncStatus || "").startsWith("Qualified") ? "Lead Qualified" : "Lead Closed";
  }
  if (!workflowAssigned && !currentStep) {
    return "Pending Workflow Assignment";
  }
  return "—";
}

export function AssignedWorkflowCard({ rnc, onViewWorkflow }) {
  const workflowAssigned = Boolean(rnc.workflowAssigned);
  const isTerminal = Boolean(rnc.exited)
    || String(rnc.status || "").startsWith("Qualified")
    || String(rnc.status || "").startsWith("Unqualified");
  const defaultStep = getWorkflowDefaultStep(isTerminal, rnc.status, workflowAssigned, rnc.currentStep);
  const curStep = rnc.currentStep || defaultStep;
  const nextStep = rnc.nextStep || (isTerminal || !workflowAssigned ? "None" : "Qualification Decision");
  const remaining = rnc.remainingTime || "—";
  const source = chInfo(rnc.channel);
  const hasSource = Boolean(rnc.channel);

  return (
    <div className="card" data-testid="assigned-workflow-card">
      <h3><GitBranch size={16} /> Assigned Workflow</h3>
      <div className="field-row">
        <div className="field">
          <span className="field-label">CAMPAIGN</span>
          <div className="val">{rnc.campaignName || "N/A"}</div>
        </div>
        <div className="field">
          <span className="field-label">SOURCE</span>
          <div className="val" style={{ display: "flex", alignItems: "center", gap: 7 }} data-testid="assigned-workflow-source">
            {hasSource ? (
              <>
                <ChannelBadge channelKey={rnc.channel} size={18} /> {source.name}
              </>
            ) : (
              "N/A"
            )}
          </div>
        </div>
      </div>
      <div className="field-row">
        <div className="field"><span className="field-label">ASSIGNED DATE</span><div className="val">{workflowAssigned ? formatDateTime(rnc.assignedDate, "N/A") : "Not Assigned"}</div></div>
        <div className="field">
          <span className="field-label">STATUS</span>
          <div className="val">
            {rnc.status ? (
              <span className={`badge sm ${statusColor(rnc.status)}`}><span className="dotb" />{rnc.status}</span>
            ) : (
              "N/A"
            )}
          </div>
        </div>
      </div>
      <div className="field-row">
        <div className="field"><span className="field-label">CURRENT STEP</span><div className="val">{curStep}</div></div>
        <div className="field"><span className="field-label">NEXT STEP</span><div className="val">{nextStep}</div></div>
      </div>
      <div className="field"><span className="field-label">REMAINING TIME</span><div className="val">{remaining}</div></div>
      <button type="button" className="btn" style={{ marginTop: 6 }} onClick={onViewWorkflow} data-testid="view-workflow-btn">
        <Eye size={14} /> View Workflow
      </button>
    </div>
  );
}

AssignedWorkflowCard.propTypes = {
  rnc: PropTypes.object.isRequired,
  onViewWorkflow: PropTypes.func.isRequired,
};

function renderTerminalIcon(isLast, finalTone) {
  if (!isLast) {
    return <Check size={12} strokeWidth={3} />;
  }
  if (finalTone === "green") {
    return <CheckCircle2 size={14} />;
  }
  return <XCircle size={14} />;
}

export function TerminalTimeline({ steps, finalTone }) {
  const isGreen = finalTone === "green";
  return (
    <div className="timeline">
      {steps.map((s, i) => {
        const isLast = i === steps.length - 1;
        const nodeStyle = isLast
          ? {
            background: isGreen ? "var(--green-50)" : "var(--red-50)",
            color: isGreen ? "var(--green-600)" : "var(--red-600)",
            border: `2px solid ${isGreen ? "var(--green-200)" : "var(--red-200)"}`,
          }
          : undefined;
        const titleStyle = isLast
          ? { color: isGreen ? "var(--green-600)" : "var(--red-600)" }
          : undefined;

        return (
          <div className={`tstep ${isLast ? "" : "done"}`} key={s} style={isLast ? { paddingBottom: 0 } : undefined}>
            <div className={`node ${isLast ? "" : "done"}`} style={nodeStyle}>
              {renderTerminalIcon(isLast, finalTone)}
            </div>
            <div className="body">
              <div className="row1">
                <span className="ttl" style={titleStyle}>
                  {s}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

TerminalTimeline.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  finalTone: PropTypes.oneOf(["green", "red"]).isRequired,
};
